import type { ContextMenuItem, ContextMenuPosition } from "@amoreg/contracts";

const SVG_NS = "http://www.w3.org/2000/svg";

// Inline Lucide-style icon paths (stroke-based, viewBox 0 0 24 24, strokeWidth 2).
const ICON_PATHS: Record<string, ReadonlyArray<{ tag: string; attrs: Record<string, string> }>> = {
  pencil: [
    {
      tag: "path",
      attrs: {
        d: "M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z",
      },
    },
    { tag: "path", attrs: { d: "m15 5 4 4" } },
  ],
  copy: [
    { tag: "rect", attrs: { width: "14", height: "14", x: "8", y: "8", rx: "2", ry: "2" } },
    { tag: "path", attrs: { d: "M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" } },
  ],
  "folder-tree": [
    {
      tag: "path",
      attrs: {
        d: "M20 10a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-2.5a1 1 0 0 1-.8-.4l-.9-1.2A1 1 0 0 0 15 3h-2a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1Z",
      },
    },
    {
      tag: "path",
      attrs: {
        d: "M20 21a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1h-2.9a1 1 0 0 1-.88-.55l-.42-.85a1 1 0 0 0-.92-.6H13a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1Z",
      },
    },
    { tag: "path", attrs: { d: "M3 5a2 2 0 0 0 2 2h3" } },
    { tag: "path", attrs: { d: "M3 3v13a2 2 0 0 0 2 2h3" } },
  ],
  trash: [
    { tag: "path", attrs: { d: "M3 6h18" } },
    { tag: "path", attrs: { d: "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" } },
    { tag: "path", attrs: { d: "M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" } },
    { tag: "line", attrs: { x1: "10", x2: "10", y1: "11", y2: "17" } },
    { tag: "line", attrs: { x1: "14", x2: "14", y1: "11", y2: "17" } },
  ],
};

function createIconElement(name: string, tone: "neutral" | "destructive"): SVGSVGElement | null {
  const paths = ICON_PATHS[name];
  if (!paths) {
    return null;
  }
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("xmlns", SVG_NS);
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute(
    "class",
    tone === "destructive" ? "size-3.5 shrink-0" : "size-3.5 shrink-0 text-muted-foreground",
  );
  for (const node of paths) {
    const child = document.createElementNS(SVG_NS, node.tag);
    for (const [key, value] of Object.entries(node.attrs)) {
      child.setAttribute(key, value);
    }
    svg.appendChild(child);
  }
  return svg;
}

function clampMenuPosition(menu: HTMLDivElement, preferredLeft: number, preferredTop: number) {
  const rect = menu.getBoundingClientRect();
  const left = Math.min(
    Math.max(4, preferredLeft),
    Math.max(4, window.innerWidth - rect.width - 4),
  );
  const top = Math.min(
    Math.max(4, preferredTop),
    Math.max(4, window.innerHeight - rect.height - 4),
  );
  menu.style.left = `${left}px`;
  menu.style.top = `${top}px`;
}

function isNodeWithinMenuStack(target: EventTarget | null, menuStack: readonly HTMLDivElement[]) {
  if (typeof Node !== "undefined" && target instanceof Node) {
    return menuStack.some((menu) => menu.contains(target));
  }
  if (!target || typeof target !== "object") {
    return false;
  }

  let current: unknown = target;
  while (current && typeof current === "object") {
    if (menuStack.includes(current as HTMLDivElement)) {
      return true;
    }
    current = (current as { parent?: unknown }).parent;
  }
  return false;
}

// Only one fallback menu exists at a time in the renderer; the active one is
// tracked so a state change (for example a terminal selection clearing) can
// dismiss it with the same result as an outside click or Escape.
let activeContextMenuDismiss: (() => void) | null = null;

/**
 * Closes the currently open fallback context menu, resolving its show() with
 * null (the same result as dismissing by outside click or Escape). No-op when
 * no fallback menu is open.
 */
export function dismissContextMenu(): void {
  activeContextMenuDismiss?.();
  activeContextMenuDismiss = null;
}

/**
 * Imperative DOM-based context menu for non-Electron environments.
 * Supports nested submenus and resolves with the clicked leaf item id.
 * Opened from the keyboard, it takes focus on its first item: the arrows,
 * Home and End move it, as does the pointer, Enter or Space choose it or open
 * its submenu, Right opens and Left or Escape close a submenu, and Escape,
 * Tab or a choice give focus back to what had it. Opened by the pointer,
 * focus stays where it is and only Escape is its key, so a popup offered
 * after a selection never takes the typing.
 */
export function showContextMenuFallback<T extends string>(
  items: readonly ContextMenuItem<T>[],
  position?: ContextMenuPosition,
): Promise<T | null> {
  return new Promise<T | null>((resolve) => {
    const menuStack: HTMLDivElement[] = [];
    /** The item that opened each submenu, by the submenu's level. */
    const openers: HTMLButtonElement[] = [];
    const submenuOf = new Map<HTMLButtonElement, () => void>();
    const choiceOf = new Map<HTMLButtonElement, T>();
    const takesFocus = position?.fromKeyboard === true;
    const returnFocus = takesFocus ? document.activeElement : null;
    let isDisposed = false;
    let canDismissFromPointer = false;

    const dismiss = () => cleanup(null);

    const focusBackAndCleanup = (result: T | null) => {
      cleanup(result);
      if (returnFocus instanceof HTMLElement && returnFocus.isConnected) returnFocus.focus({ preventScroll: true });
    };

    const cleanup = (result: T | null) => {
      if (isDisposed) {
        return;
      }
      isDisposed = true;
      if (activeContextMenuDismiss === dismiss) {
        activeContextMenuDismiss = null;
      }
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("contextmenu", onContextMenu, true);
      for (const menu of menuStack) {
        menu.remove();
      }
      resolve(result);
    };

    const enabledItems = (menu: HTMLDivElement | undefined) =>
      menu ? [...menu.querySelectorAll("button")].filter((button) => !button.disabled) : [];

    const focusInto = (menu: HTMLDivElement | undefined) => (enabledItems(menu)[0] ?? menu)?.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (!takesFocus) {
        if (event.key === "Escape") {
          event.preventDefault();
          cleanup(null);
        }
        return;
      }
      const level = menuStack.findIndex((menu) => menu.contains(document.activeElement));
      const opener = openers[level];
      if (event.key === "Escape") {
        event.preventDefault();
        if (level > 0 && opener) closeMenusFromLevel(level);
        else focusBackAndCleanup(null);
        return;
      }
      const menu = menuStack[level];
      if (!menu) return;
      if (event.key === "Tab") {
        event.preventDefault();
        focusBackAndCleanup(null);
        return;
      }
      const items = enabledItems(menu);
      const at = items.findIndex((item) => item === document.activeElement);
      const openSubmenu = at >= 0 ? submenuOf.get(items[at]!) : undefined;
      let next: HTMLElement | undefined;
      if (event.key === "ArrowDown") next = items[(at + 1) % items.length];
      else if (event.key === "ArrowUp") next = items[(Math.max(at, 0) - 1 + items.length) % items.length];
      else if (event.key === "Home") next = items[0];
      else if (event.key === "End") next = items.at(-1);
      else if (openSubmenu && (event.key === "ArrowRight" || event.key === "Enter" || event.key === " ")) {
        openSubmenu();
        focusInto(menuStack[level + 1]);
      } else if (at >= 0 && (event.key === "Enter" || event.key === " ") && choiceOf.has(items[at]!)) {
        // A key can't be the opening gesture's stray click, so it skips the pointer's first-frame guard.
        focusBackAndCleanup(choiceOf.get(items[at]!)!);
      } else if (event.key === "ArrowLeft" && opener) {
        closeMenusFromLevel(level);
      } else return;
      // The menu owns these keys: nothing behind it (a list's arrows, a shortcut) hears them.
      event.preventDefault();
      event.stopPropagation();
      // The menu is fixed, so the only scroll this can move is its own list's, revealing an item past its fold.
      next?.focus();
    };

    const onPointerDown = (event: PointerEvent) => {
      if (!canDismissFromPointer || isNodeWithinMenuStack(event.target, menuStack)) {
        return;
      }
      cleanup(null);
    };

    const onContextMenu = (event: MouseEvent) => {
      // Inside the menu (the ContextMenu key's own event lands on the focused item), never the browser's menu.
      if (isNodeWithinMenuStack(event.target, menuStack)) {
        event.preventDefault();
        return;
      }
      if (!canDismissFromPointer) {
        return;
      }
      event.preventDefault();
      cleanup(null);
    };

    // Closing the submenu that holds focus hands focus to the item that opened it.
    const closeMenusFromLevel = (level: number) => {
      const opener = openers[level];
      const heldFocus = menuStack.slice(level).some((menu) => menu.contains(document.activeElement));
      while (menuStack.length > level) {
        menuStack.pop()?.remove();
        openers.pop()?.setAttribute("aria-expanded", "false");
      }
      if (heldFocus) opener?.focus({ preventScroll: true });
    };

    const openMenu = (
      entries: readonly ContextMenuItem<T>[],
      preferredLeft: number,
      preferredTop: number,
      level: number,
    ) => {
      closeMenusFromLevel(level);

      const menu = document.createElement("div");
      menu.className =
        "dropdown-glass fixed z-[10000] min-w-32 max-w-sm overflow-hidden rounded-lg bg-clip-padding text-popover-foreground outline-none";
      menu.style.cssText =
        "position:fixed;z-index:10000;min-width:8rem;max-width:24rem;overflow:hidden;border-radius:var(--radius-lg);background-clip:padding-box;color:var(--popover-foreground);outline:none;pointer-events:auto;";
      menu.style.left = `${preferredLeft}px`;
      menu.style.top = `${preferredTop}px`;
      menu.dataset.level = String(level);
      menu.setAttribute("role", "menu");
      menu.tabIndex = -1;

      const inner = document.createElement("div");
      inner.className =
        "max-h-[min(24rem,70vh)] min-w-0 max-w-sm overflow-y-auto overflow-x-hidden p-1";
      inner.style.cssText =
        "max-height:min(24rem,70vh);min-width:0;max-width:24rem;overflow-x:hidden;overflow-y:auto;padding:0.25rem;";

      for (const item of entries) {
        if (item.header === true) {
          const header = document.createElement("div");
          header.className = "px-2 py-1.5 font-medium text-muted-foreground text-xs";
          header.textContent = item.label;
          inner.appendChild(header);
          continue;
        }

        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
        const isLeafDestructive =
          !hasChildren && (item.destructive === true || item.id === ("delete" as T));

        const button = document.createElement("button");
        button.type = "button";
        button.setAttribute("role", "menuitem");
        button.tabIndex = -1;
        const isDisabled = item.disabled === true;
        button.disabled = isDisabled;
        const rowBase =
          "flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1 text-left outline-none transition-colors sm:min-h-7 sm:text-sm min-h-8 text-base";
        button.className = isDisabled
          ? `${rowBase} pointer-events-none cursor-not-allowed text-muted-foreground opacity-64`
          : isLeafDestructive
            ? `${rowBase} text-destructive-foreground hover:bg-destructive/10 hover:text-destructive-foreground`
            : `${rowBase} text-foreground hover:bg-accent hover:text-accent-foreground`;
        button.style.cssText =
          "display:flex;width:100%;min-height:1.75rem;align-items:center;gap:0.5rem;border:0;border-radius:var(--radius-sm);background:transparent;padding:0.25rem 0.5rem;color:var(--foreground);font-family:var(--font-sans,system-ui,sans-serif);font-size:0.875rem;line-height:1.25rem;text-align:left;cursor:default;";
        if (isLeafDestructive) {
          button.style.color = "var(--destructive-foreground)";
        }
        if (isDisabled) {
          button.style.color = "var(--muted-foreground)";
          button.style.opacity = "0.64";
          button.style.pointerEvents = "none";
        }

        if (typeof item.icon === "string") {
          const icon = createIconElement(item.icon, isLeafDestructive ? "destructive" : "neutral");
          if (icon) {
            button.appendChild(icon);
          }
        }

        const label = document.createElement("span");
        label.className = "min-w-0 flex-1 truncate";
        label.textContent = item.label;
        button.appendChild(label);

        if (hasChildren) {
          const chevron = document.createElement("span");
          chevron.className = "ms-auto shrink-0 text-muted-foreground/80 text-sm leading-none";
          chevron.setAttribute("aria-hidden", "true");
          chevron.textContent = ">";
          button.appendChild(chevron);
        }

        if (!isDisabled) {
          let hovered = false;
          let focused = false;
          const paint = () => {
            const active = hovered || focused;
            button.style.background = active
              ? isLeafDestructive
                ? "color-mix(in srgb, var(--destructive) 10%, transparent)"
                : "var(--accent)"
              : "transparent";
            button.style.color = isLeafDestructive
              ? "var(--destructive-foreground)"
              : active
                ? "var(--accent-foreground)"
                : "var(--foreground)";
          };
          button.addEventListener("mouseenter", () => {
            hovered = true;
            paint();
          });
          button.addEventListener("mouseleave", () => {
            hovered = false;
            paint();
          });
          if (takesFocus) {
            // The pointer moves the keyboard's focus too, so one item is ever the active one.
            button.addEventListener("mouseenter", () => button.focus({ preventScroll: true }));
            button.addEventListener("focus", () => {
              focused = true;
              paint();
            });
            button.addEventListener("blur", () => {
              focused = false;
              paint();
            });
          }

          if (hasChildren) {
            button.setAttribute("aria-haspopup", "menu");
            button.setAttribute("aria-expanded", "false");
            const openSubmenu = () => {
              const rect = button.getBoundingClientRect();
              const nextLeft = rect.right + 4;
              const nextTop = rect.top;
              openMenu(item.children!, nextLeft, nextTop, level + 1);
              openers[level + 1] = button;
              button.setAttribute("aria-expanded", "true");

              const childMenu = menuStack[level + 1];
              if (!childMenu) {
                return;
              }
              const childRect = childMenu.getBoundingClientRect();
              if (childRect.right > window.innerWidth) {
                clampMenuPosition(childMenu, rect.left - childRect.width - 4, rect.top);
              }
            };
            submenuOf.set(button, openSubmenu);
            button.addEventListener("mouseenter", openSubmenu);
            button.addEventListener("click", (event) => {
              event.preventDefault();
            });
          } else {
            button.addEventListener("mouseenter", () => {
              closeMenusFromLevel(level + 1);
            });
            choiceOf.set(button, item.id);
            button.addEventListener("click", () => {
              if (canDismissFromPointer) focusBackAndCleanup(item.id);
            });
          }
        }

        inner.appendChild(button);
      }

      menu.appendChild(inner);

      menu.addEventListener("mouseenter", () => {
        closeMenusFromLevel(level + 1);
      });

      document.body.appendChild(menu);
      menuStack[level] = menu;

      requestAnimationFrame(() => {
        clampMenuPosition(menu, preferredLeft, preferredTop);
      });
    };

    // Capture, so the Escape is the menu's before React's root hears it: a
    // panel that closes on an Escape nobody handled stays open.
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("contextmenu", onContextMenu, true);
    openMenu(items, position?.x ?? 0, position?.y ?? 0, 0);
    if (position?.fromKeyboard) focusInto(menuStack[0]);
    // Only one fallback menu can be open at a time: a new show must dismiss
    // any prior one, or its DOM and listeners leak and close() can only ever
    // reach the newest menu.
    if (activeContextMenuDismiss) {
      activeContextMenuDismiss();
    }
    activeContextMenuDismiss = dismiss;

    requestAnimationFrame(() => {
      canDismissFromPointer = true;
    });
  });
}
